import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck, CalendarDays, Clock3, Euro } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const metrics = [
  { icon: Clock3, value: "130 ore", label: "Programma abilitante" },
  { icon: Euro, value: "1.000 €", label: "Tutto incluso" },
  { icon: BadgeCheck, value: "Valido in tutta Italia", label: "Esame finale abilitante" },
  { icon: CalendarDays, value: "26 Ott 2026", label: "Prossima edizione" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" ref={ref} className="grain relative overflow-hidden bg-paper pt-16" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-16 pt-12 sm:px-6 md:pt-20 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:pb-24">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-brand-amber/40 bg-brand-light px-3 py-1.5"
            data-testid="hero-edition-pill"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-amber" />
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-amber-800">
              Iscrizioni aperte · Edizione 26 Ottobre 2026
            </span>
          </motion.div>

          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <Line delay={0.2}>Corso SAB <span className="font-medium text-slate-400">(ex REC)</span></Line>
            <Line delay={0.32}>Somministrazione</Line>
            <Line delay={0.44}>
              Alimenti <span className="text-brand-amber">e Bevande</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subtitle"
          >
            L'abilitazione obbligatoria per aprire o gestire bar, ristoranti, tavole calde,
            supermercati e ogni attività di vendita o somministrazione di alimenti e bevande.
            In presenza a Milano oppure online.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#iscriviti")}
              className="group flex items-center gap-2 bg-navy px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-amber"
              data-testid="hero-cta-button"
            >
              Prenota il tuo posto
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              href="tel:+393513937976"
              className="border border-navy px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white"
              data-testid="hero-call-button"
            >
              Chiama ora
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 grid grid-cols-2 gap-px border border-line bg-line sm:grid-cols-4"
            data-testid="hero-metrics"
          >
            {metrics.map((m) => (
              <div key={m.label} className="bg-white p-4">
                <m.icon className="h-4 w-4 text-brand-amber" />
                <p className="mt-2 font-heading text-sm font-bold text-navy sm:text-base">{m.value}</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">{m.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] overflow-hidden"
          >
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src="https://images.pexels.com/photos/32981670/pexels-photo-32981670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
              alt="Ristorante e locale bar moderno a Milano"
              className="h-full w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="absolute -bottom-6 -left-6 hidden bg-navy p-5 text-white shadow-2xl sm:block"
            data-testid="hero-posti-badge"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber">Posti limitati</p>
            <p className="mt-1 font-heading text-lg font-bold">Frequenza obbligatoria</p>
            <p className="text-xs text-slate-300">Sede: Via Mazzali 5, Milano</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
